import { useCallback, useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { isEmpty, isEqual, isNil, size } from "lodash";

import { Box, IconButton, InputBase } from "@mui/material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ForumIcon from "@mui/icons-material/Forum";
import SaveIcon from "@mui/icons-material/Save";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import SidePanel, { PANEL, PANEL_LABEL_SX, PanelPrimaryButton } from "@plannel/components/ui/SidePanel";
import dpNoteService from "@plannel/services/dp/dp-note-service";
import debug from "@plannel/utils/debug-util";
import dateUtils, { DATETIME_MINUTE_FORMAT } from "@plannel/utils/date-util";
import { FS } from "@plannel/utils/font-util";

/**
 * Demand Netting 노트.
 *
 * <p>저장소·API 는 DP 워크벤치와 같다 ({@code z_dp_note}, {@code /dp-note}). 화면도 워크벤치의
 * 노트 패널과 같은 모양이다 — 공용 {@link SidePanel} 프리미티브를 그대로 쓴다.
 *
 * <p>워크벤치의 {@code NotesModal} 을 재사용하지 않은 이유 — 그쪽은 워크벤치·RP 두 화면의
 * 셀 구조가 하드코딩돼 있어 세 번째 분기를 넣으면 기존 두 화면까지 흔들린다.
 *
 * <p><b>접두사는 네 번째(지표) 조각에 붙인다.</b> 워크벤치와 같은 계획 버전을 공유하므로 키가
 * 겹치면 서로의 노트를 덮어쓴다. 다만 <b>첫 두 조각은 조인 키다</b> —
 * {@code DpQueryUtil.tableNoteForPerm} 이 거래처 · 품목 코드로 LEFT JOIN 해 이름을 채운다.
 * 거기에 접두사를 붙이면 조인이 비어 이름이 null 이 되고, 워크벤치의 노트 정렬이
 * NPE 로 죽는다(2026-09-16 실제 발생). 지표 조각은 조인에 쓰이지 않아 안전하고,
 * 워크벤치의 지표 코드(MF_QTY 등)와 겹칠 일도 없다.
 */
const SPLITTER = "#SPLIT#";
const PREFIX = "Netting_";

/**
 * 셀 단위 노트 — DP 워크벤치와 같은 4조각 구성이다.
 *
 * <p>워크벤치는 첫 두 조각에 <b>계층 코드</b>를 넣는다(그 화면은 대분류·중분류로 접어 볼 수
 * 있어서 계층 노드에 노트가 달린다). 이 화면은 항상 말단이라 품목 · 거래처 코드를 넣는다.
 */
export const buildNoteKey = (customerCd, itemCd, baseDate, measureCd) =>
  [customerCd ?? "", itemCd ?? "", baseDate ?? "", `${PREFIX}${measureCd ?? ""}`].join(SPLITTER);

/** 작성자 머리글자. 공백으로 나뉘면 두 단어의 첫 글자, 아니면 앞 두 글자. */
const getInitials = name => {
  const parts = String(name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : parts[0].slice(0, 2)).toUpperCase();
};

const DemandNettingNoteModal = props => {
  const { t, open, close, target, version, onSaved } = props;

  const [content, setContent] = useState("");
  const [notes, setNotes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const noteKey = target
    ? buildNoteKey(target.customerCd, target.itemCd, target.baseDate, target.measureCd)
    : null;

  const load = useCallback(() => {
    if (!open || isNil(noteKey) || isNil(version?.id)) return;
    dpNoteService.getNote({ versionDto: version, noteKey })
      .then(({ data, status }) => {
        // 노트가 없으면 서버가 204 를 준다 - 오류가 아니다
        setNotes(status === 204 || isNil(data) ? [] : (data.noteResult ?? []));
      })
      .catch(e => { debug(e); setNotes([]); });
  }, [open, noteKey, version]);

  useEffect(() => { load(); }, [load]);

  // 패널을 닫으면 작성 중이던 글과 선택을 버린다 - 다음에 열 때 남아 있으면 오해를 만든다
  useEffect(() => {
    if (!open) { setContent(""); setSelected(null); }
  }, [open]);

  /** 쓰고 난 뒤 다시 읽어, 이 조합에 노트가 남았는지를 호출부에 알린다(그리드 표식용). */
  const afterWrite = () => {
    setContent("");
    setSelected(null);
    if (isNil(noteKey) || isNil(version?.id)) return;
    dpNoteService.getNote({ versionDto: version, noteKey })
      .then(({ data, status }) => {
        const list = status === 204 || isNil(data) ? [] : (data.noteResult ?? []);
        setNotes(list);
        onSaved?.(noteKey, list.length > 0);
      })
      .catch(e => { debug(e); setNotes([]); });
  };

  const handleSave = () => {
    if (isNil(noteKey) || isNil(version?.id) || isEmpty(content.trim())) return;
    setSaving(true);
    dpNoteService.upsertNote({
      // 선택한 글이 있으면 수정, 없으면 새 글. seqNum 은 서버가 최대값+1 로 매긴다
      id: selected?.id,
      verNum: selected?.verNum ?? 0,
      planVersion: { id: version.id },
      noteKey,
      seqNum: selected?.seqNum ?? 0,
      contentTxt: content.trim()
    })
      .then(afterWrite)
      .catch(e => debug(e))
      .finally(() => setSaving(false));
  };

  const handleDelete = note => {
    dpNoteService.deleteNote({ ...note, planVersion: { id: version.id } })
      .then(afterWrite)
      .catch(e => debug(e));
  };

  const handlePick = note => {
    const same = isEqual(selected?.id, note.id);
    setSelected(same ? null : note);
    setContent(same ? "" : (note.contentTxt ?? ""));
  };

  if (!open) return null;

  return (
    <SidePanel
      id="demand-netting-note-panel"
      resizable
      icon={<EditNoteIcon />}
      title={t("notes")}
      subtitle={target?.measureLabel ? `${t("measure")} · ${t(target.measureLabel)}` : null}
      onClose={close}
      closeLabel={t("close")}
      bodySx={{ gap: "13px" }}>

      {/* 어느 조합의 노트인지 — 1px 간격에 회색 배경을 깔아 구분선을 만든다 */}
      <Box sx={{
        display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1px",
        bgcolor: PANEL.line, borderRadius: "12px", overflow: "hidden", flex: "none"
      }}>
        {[
          { label: t("item"), value: target?.itemCd },
          { label: t("customer"), value: target?.customerCd },
          { label: t("time"), value: target?.baseDate }
        ].map(cell => (
          <Box key={cell.label} sx={{ bgcolor: "#fafcfd", p: "10px 11px", minWidth: 0 }}>
            <Box sx={PANEL_LABEL_SX}>{cell.label}</Box>
            <Box sx={{
              mt: "3px", fontSize: FS.SUB, color: PANEL.ink,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
            }}>
              {cell.value || "—"}
            </Box>
          </Box>
        ))}
      </Box>

      {/* 작성 영역 — 테두리 없는 입력, 포커스는 카드 링으로 표시한다 */}
      <Box sx={{
        flex: "none", borderRadius: "12px", p: "13px",
        boxShadow: `inset 0 0 0 1px ${PANEL.lineSoft}`,
        transition: "box-shadow .15s ease",
        "&:focus-within": { boxShadow: `inset 0 0 0 2px ${PANEL.accentLineHover}` }
      }}>
        <InputBase
          multiline
          minRows={3}
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder={t("MSG_PleaseEnterValues", { values: t("notes") })}
          // z_dp_note.content_txt 가 VARCHAR(2000) 이다
          inputProps={{ maxLength: 2000 }}
          sx={{
            width: "100%", p: 0, fontSize: FS.TITLE, lineHeight: 1.5, color: PANEL.ink,
            "& textarea::placeholder": { color: PANEL.inkFaint, opacity: 1 }
          }}
        />
        <Box sx={{ display: "flex", alignItems: "center", mt: "12px" }}>
          <Box sx={{ flex: 1 }} />
          <PanelPrimaryButton icon={<SaveIcon />} disabled={saving || isNil(noteKey) || isEmpty(content.trim())} onClick={handleSave}>
            {t("save")}
          </PanelPrimaryButton>
        </Box>
      </Box>

      {/* 스레드 — 없으면 빈 상태를 대신 보여준다 */}
      {size(notes) > 0 ? notes.map(note => (
        <Box
          key={note.id}
          onClick={() => handlePick(note)}
          sx={{
            flex: "none", display: "flex", gap: "11px", p: "12px 13px",
            borderRadius: "12px", cursor: "pointer",
            bgcolor: isEqual(selected?.id, note.id) ? PANEL.accentWash : PANEL.wellBg,
            boxShadow: isEqual(selected?.id, note.id) ? `inset 0 0 0 1px ${PANEL.accentLine}` : "none",
            transition: "background .15s ease, box-shadow .15s ease"
          }}>
          <Box sx={{
            flex: "none", width: 30, height: 30, borderRadius: "15px",
            bgcolor: PANEL.accentDeep, color: "#fff",
            fontSize: FS.SUB, fontWeight: 500, lineHeight: "30px", textAlign: "center"
          }}>
            {getInitials(note.createdUser)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
              <Box sx={{ fontSize: FS.SUB, fontWeight: 500, color: PANEL.ink }}>{note.createdUser}</Box>
              {/* DB 원본은 초·나노초까지 있다 — 분까지만 보여 준다 */}
              <Box sx={{ fontSize: FS.CAPTION, color: PANEL.inkLabel, whiteSpace: "nowrap" }}>
                {dateUtils.formatDate(note.updatedTs, DATETIME_MINUTE_FORMAT)}
              </Box>
              <Box sx={{ flex: 1 }} />
              <IconButton
                size="small"
                onClick={e => { e.stopPropagation(); handleDelete(note); }}
                sx={{ color: PANEL.inkLabel }}>
                <DeleteOutlineIcon fontSize="inherit" />
              </IconButton>
            </Box>
            <Box sx={{ mt: "4px", fontSize: FS.TITLE, color: PANEL.ink, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {note.contentTxt}
            </Box>
          </Box>
        </Box>
      )) : (
        <Box sx={{
          flex: "none", borderRadius: "12px", p: "22px 13px", textAlign: "center",
          bgcolor: PANEL.wellBg, color: PANEL.inkFaint, fontSize: FS.SUB
        }}>
          <ForumIcon sx={{ fontSize: 28, display: "block", mx: "auto", mb: "6px", opacity: 0.6 }} />
          {isNil(noteKey) ? t("MSG_SelectNoteCell") : t("MSG_NoData")}
        </Box>
      )}
    </SidePanel>
  );
};

export default withTranslation()(DemandNettingNoteModal);
